import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useId, useMemo, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { primaryActionClass, useChatTheme } from "../components/chat-theme";
import { CONSENT_KEY } from "./constants";
import type { ActionField } from "./contract";
import { FieldControl } from "./field-control";
import { buildActionSchema, errorMessage, initialValues, type FormValues } from "./form-schema";
import type { ActionLabels } from "./labels";

export interface ActionFieldsFormProps {
  fields: readonly ActionField[];
  contactRule?: string | undefined;
  consentText: string;
  privacyUrl?: string | undefined;
  title?: ReactNode;
  ariaLabel?: string;
  submitLabel: string;
  submitting: boolean;
  /** Preview: fields are shown, nothing can be sent. */
  disabled?: boolean;
  labels: ActionLabels;
  /** Fields the server rejected: marked and the first one focused. */
  rejected: readonly string[];
  /** Failure message / retry area, above the bot check and the submit button. */
  notice?: ReactNode;
  captcha?: ReactNode;
  onSubmit: (values: FormValues) => void;
}

function cleanValues(fields: readonly ActionField[], values: Record<string, unknown>): FormValues {
  return Object.fromEntries(fields.map((field) => [field.name, typeof values[field.name] === "string" ? String(values[field.name]) : ""]));
}

/** Server-described fields + privacy consent + submit (zod-validated; shared by the lead and booking cards). */
export function ActionFieldsForm({ fields, contactRule, consentText, privacyUrl, title, ariaLabel, submitLabel, submitting, disabled, labels, rejected, notice, captcha, onSubmit }: ActionFieldsFormProps) {
  const { themed } = useChatTheme();
  const id = useId();
  const schema = useMemo(() => buildActionSchema(fields, labels, contactRule), [fields, labels, contactRule]);
  const form = useForm<Record<string, unknown>>({ resolver: zodResolver(schema), defaultValues: initialValues(fields) });
  const { errors } = form.formState;
  const { setError, setFocus } = form;

  useEffect(() => {
    const known = rejected.filter((name) => fields.some((field) => field.name === name));
    for (const name of known) setError(name, { type: "server", message: labels.checkField });
    const first = known[0];
    if (first) setFocus(first);
  }, [rejected, fields, labels.checkField, setError, setFocus]);

  const submit = form.handleSubmit((values) => {
    onSubmit(cleanValues(fields, values));
  });

  return (
    <form noValidate aria-labelledby={title ? `${id}-title` : undefined} aria-label={title ? undefined : ariaLabel} onSubmit={(event) => void submit(event)}>
      {title ? (
        <div id={`${id}-title`} className="mb-2 text-xs font-bold text-fg">
          {title}
        </div>
      ) : null}
      <fieldset disabled={disabled} className="min-w-0 space-y-3 border-0 p-0">
        {fields.map((field) => (
          <FieldControl key={field.name} field={field} register={form.register} error={errors[field.name]} labels={labels} />
        ))}
        <div>
          <label className="flex min-h-11 items-start gap-2 py-1 text-xs text-fg-muted">
            <input
              type="checkbox"
              className="checkbox mt-0.5 size-5 shrink-0"
              aria-invalid={errors[CONSENT_KEY] ? true : undefined}
              aria-describedby={errors[CONSENT_KEY] ? `${id}-consent-error` : undefined}
              {...form.register(CONSENT_KEY)}
            />
            <span>
              {consentText || labels.consent}
              {privacyUrl ? (
                <>
                  {" "}
                  <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-ink underline">
                    {labels.privacyLink}
                  </a>
                </>
              ) : null}
            </span>
          </label>
          {errors[CONSENT_KEY] ? (
            <p id={`${id}-consent-error`} role="alert" className="mt-1 text-xs font-semibold text-danger">
              {errorMessage(errors[CONSENT_KEY])}
            </p>
          ) : null}
        </div>
        {notice}
        {captcha}
        <button type="submit" disabled={submitting} aria-busy={submitting || undefined} className={`${primaryActionClass(themed)} min-h-11 w-full`}>
          {submitting ? labels.sending : submitLabel}
        </button>
      </fieldset>
    </form>
  );
}
